import React, { useState, useEffect } from 'react';
import { Tabs as MaestroTabs, Skeleton, Group } from '@landr/maestro';
import { ContentList } from 'types/generated/graphql';
import { Pack, RePack } from 'types';
import { usePreviewContext } from '../contexts/PreviewContext/PreviewContext';
import Carousel from './Carousel';

const TabsSkeleton = () => (
    <Group gutter="sm">
        <Group.Item>
            <Skeleton width="70px" height="36px" variant="round" />
        </Group.Item>
        <Group.Item>
            <Skeleton width="50px" height="36px" variant="round" />
        </Group.Item>
        <Group.Item>
            <Skeleton width="60px" height="36px" variant="round" />
        </Group.Item>
    </Group>
);

const getContentListHasItem = (contentLists?: ContentList[]) =>
    !!contentLists &&
    contentLists.some(({ tabName, items }) => tabName && items?.length > 0);

const Tabs = ({
    contentLists,
    isLoading,
}: {
    contentLists?: ContentList[];
    isLoading: boolean;
}) => {
    const [userSelectedTab, setUserSelectedTab] = useState<string>('');
    const { setSelectedContentList } = usePreviewContext();

    useEffect(
        function setInitialSelectedTab() {
            if (!contentLists) {
                return;
            }

            const [firstContentList] = contentLists;
            const selectedTab = firstContentList?.tabName;

            setUserSelectedTab(selectedTab);
            setSelectedContentList(selectedTab);
        },
        [contentLists, setSelectedContentList],
    );

    const onSelectTab = (tabName: string) => {
        setUserSelectedTab(tabName);
        setSelectedContentList(tabName);
    };

    const showSkeleton =
        isLoading || !getContentListHasItem(contentLists) || !contentLists;

    if (showSkeleton) {
        return (
            <Group gutter="xl" direction="vertical" mb="mg">
                <Group.Item>
                    <TabsSkeleton />
                </Group.Item>
                <Group.Item>
                    <Carousel items={[] as Pack[]} isLoading={true} />
                </Group.Item>
            </Group>
        );
    }

    const withTabs = contentLists.length > 1;

    if (!withTabs) {
        return (
            <>
                {contentLists?.map(({ items }) => {
                    if (items?.length === 0) {
                        return null;
                    }

                    return (
                        <Carousel
                            items={items as unknown as Pack[] | RePack[]}
                        />
                    );
                })}
            </>
        );
    }

    return (
        <MaestroTabs
            variant="pill"
            isLazy={true}
            onSelectTab={onSelectTab}
            selectedTab={userSelectedTab}
        >
            {contentLists?.map(({ tabName, items }) => {
                if (!tabName || items?.length === 0) {
                    return null;
                }

                return (
                    <MaestroTabs.Pane
                        key={tabName}
                        label={tabName}
                        name={tabName}
                    >
                        <Carousel
                            items={items as unknown as Pack[] | RePack[]}
                        />
                    </MaestroTabs.Pane>
                );
            })}
        </MaestroTabs>
    );
};

export default Tabs;
